import {
    Button,
    DisplayOrInput,
    Field,
    FingerprintDisplay,
    Input,
    Modal,
    ModalFooter,
    ModalHeader,
    RoleType,
} from "modules/common";
import { ConfirmationModal } from "modules/common/components/ConfirmationModal";
import { useContext, useEffect, useMemo, useState } from "react";
import {
    useBulkEditEducationDocument,
    useCreateEducationDocument,
    useDeleteEducationDocument,
    useEditEducationDocument,
    useGetEducationDocumentCategoriesAsItems,
} from "..";
import {
    EducationDocument,
    EducationDocumentPaginatedResponse,
} from "services/educationDocuments/response";
import { ReactComponent as SearchIcon } from "assets/icons/search.svg";
import SearchableDropdown from "modules/common/components/SearchableDropdown/SearchableDropdown";
import { Dropzone } from "modules/common/components/Dropzone";
import { RootState } from "store";
import { useSelector } from "react-redux";
import { DocumentType, catName } from "modules/educationDocuments/constants";
import { useGetProfessionalAreasAsItems } from "modules/codebooks";
import { isSuperAdmin } from "modules/auth/authorization/roleChecks";
import { AccessibilityContext } from "views/AccessibilityContext";

export interface IEducationDocumentDetailsModal {
    educationDocuments?: EducationDocument[];
    isOpen: boolean;
    handleClose: () => void;
    educationDocumentsData?: EducationDocumentPaginatedResponse;
    isChecked?: boolean;
    page?: number;
    onPageChange?: (page: number) => void;
}

type EducationDocumentWithFile = EducationDocument & {
    uploadedFile: File | null;
};

const emptyEducationDocument: EducationDocumentWithFile = {
    guid: "",
    name: "",
    fileUrl: "",
    documentType: DocumentType.Unknown,
    categoryGuid: "",
    professionalAreaGuid: "",
    createdBy: "",
    created: "",
    lastUpdatedBy: "",
    lastUpdated: "",
    categoryName: "",
    uploadedFile: null,
};

type EducationDocumentProperty = keyof EducationDocumentWithFile;

type EducationDocumentErrors = Partial<Record<EducationDocumentProperty, string>>;

export const EducationDocumentDetailsModal = ({
    educationDocuments: selectedEducationDocuments,
    isOpen,
    handleClose,
    educationDocumentsData,
    isChecked = false,
    page,
    onPageChange,
}: IEducationDocumentDetailsModal) => {
    const [educationDocument, setEducationDocument] =
        useState<EducationDocumentWithFile>(emptyEducationDocument);
    const [errors, setErrors] = useState<EducationDocumentErrors>({});
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [showDelete, setShowDelete] = useState(false);
    const create = useCreateEducationDocument(educationDocument.uploadedFile);
    const edit = useEditEducationDocument(educationDocument.uploadedFile);
    const bulkEdit = useBulkEditEducationDocument();
    const remove = useDeleteEducationDocument();
    const { profile } = useSelector((state: RootState) => state.profile);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [isNewCategory, setIsNewCategory] = useState(false);

    const educationDocumentCategoriesAsItems = useGetEducationDocumentCategoriesAsItems();

    const educationDocumentCategory = useMemo(() => {
        return educationDocumentCategoriesAsItems?.find(
            item => item.id === educationDocument?.categoryGuid
        );
    }, [educationDocumentCategoriesAsItems, educationDocument.categoryGuid]);

    const professionalAreasAsItems = useGetProfessionalAreasAsItems();

    const professionalArea = useMemo(() => {
        return professionalAreasAsItems?.find(
            item => item.guid === educationDocument?.professionalAreaGuid
        );
    }, [professionalAreasAsItems, educationDocument.professionalAreaGuid]);

    const documentNames = educationDocumentsData?.items.map((doc: EducationDocument) => doc.name);

    const validateEducationDocument = (
        educationDocument: EducationDocumentWithFile
    ): EducationDocumentErrors => {
        const errors: EducationDocumentErrors = {};
        if (!isBulkEdit && !educationDocument.name.trim()) {
            errors.name = "Naziv dokumenta je obavezan";
        }
        if (
            documentNames?.includes(educationDocument.name) &&
            educationDocument.name !== selectedEducationDocuments?.[0]?.name
        ) {
            errors.name = "Dokument s ovim imenom već postoji";
        }
        if (!educationDocument.categoryGuid) {
            errors.categoryGuid = "Kategorija je obavezna";
        }
        if (!educationDocument.professionalAreaGuid) {
            errors.professionalAreaGuid = "Stručno područje je obavezno";
        }
        if (educationDocument.uploadedFile == null && !isBulkEdit && isAdd) {
            errors.fileUrl = "Datoteka je obavezna";
        }
        if (!educationDocument.categoryName?.trim() && isNewCategory) {
            errors.categoryName = "Naziv kategorije je obavezan";
        }
        return errors;
    };

    useEffect(() => {
        if (!selectedEducationDocuments) {
            return;
        }
        if (selectedEducationDocuments.length === 1) {
            setEducationDocument({
                ...selectedEducationDocuments[0],
                uploadedFile: null,
            });
        }
    }, [selectedEducationDocuments, setEducationDocument]);

    useEffect(() => {
        setErrors({});
        setIsEdit(
            selectedEducationDocuments?.length ? selectedEducationDocuments.length > 1 : false
        );
        if (!isOpen) {
            setEducationDocument(emptyEducationDocument);
        }
    }, [isOpen, setErrors, setIsEdit, selectedEducationDocuments]);

    const isAdd = useMemo(
        () => selectedEducationDocuments?.length === 0,
        [selectedEducationDocuments]
    );
    const isBulkEdit = useMemo(
        () => (selectedEducationDocuments ? selectedEducationDocuments?.length > 1 : false),
        [selectedEducationDocuments]
    );

    const title = useMemo(() => {
        if (isAdd) {
            return "Novi dokument";
        }
        if (isEdit || isChecked) {
            return "Uredi dokument";
        }
        return educationDocument?.name ?? "Detalji dokumenta";
    }, [isAdd, isEdit, isChecked, educationDocument?.name]);

    const changeEducationDocumentProperty = (
        property: EducationDocumentProperty,
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setErrors(prevState => ({ ...prevState, [property]: undefined }));
        setEducationDocument(prevState => ({
            ...(prevState ?? emptyEducationDocument),
            [property]: event.target.value,
        }));
    };

    const changeEducationDocumentDropdownProperty = (
        property: EducationDocumentProperty,
        value: { value: string; label: string }
    ) => {
        setErrors(prevState => ({ ...prevState, [property]: undefined }));
        setEducationDocument(prevState => ({
            ...(prevState ?? emptyEducationDocument),
            [property]: value.value,
        }));
    };

    const isValid = (property: EducationDocumentProperty) => {
        return !errors[property];
    };

    const getErrorMsg = (property: EducationDocumentProperty) => {
        return errors[property] ?? "";
    };

    const onCancel = () => {
        setIsEdit(false);
        setErrors({});
        setEducationDocument(emptyEducationDocument);
        handleClose();
    };

    const onSave = async () => {
        const newErrors = validateEducationDocument(educationDocument);
        if (Object.keys(newErrors).length) {
            setErrors(newErrors);
            return;
        }
        setIsButtonDisabled(true);
        if (isBulkEdit) {
            const updatedDocuments = (selectedEducationDocuments ?? []).map(doc => ({
                ...doc,
                categoryGuid: educationDocument.categoryGuid,
                professionalAreaGuid: educationDocument.professionalAreaGuid,
                categoryName: educationDocument.categoryName,
            }));

            await bulkEdit.mutateAsync(updatedDocuments);
        } else if (isAdd) {
            await create.mutateAsync(educationDocument);
        } else {
            await edit.mutateAsync(educationDocument);
        }

        setErrors({});
        handleClose();
        setIsButtonDisabled(false);
    };

    const onDelete = async () => {
        if (!educationDocument) {
            return;
        }
        const remainingItems = (selectedEducationDocuments?.length ?? 0) - 1;
        if (remainingItems <= 0 && (page ?? 1) > 1) {
            onPageChange && onPageChange(1);
        }
        await remove.mutateAsync(educationDocument.guid);
        setShowDelete(false);
        handleClose();
    };

    const { lowVision } = useContext(AccessibilityContext);
    return (
        <Modal
            isActive={isOpen}
            className="education-document-details-modal"
            onClose={handleClose}
            modalHeader={<ModalHeader onClose={handleClose} title={title} />}
            modalFooter={
                isSuperAdmin(profile?.roleType?.value ?? 0) &&
                (!educationDocument ? null : (
                    <ModalFooter>
                        {isAdd || isEdit || isChecked ? (
                            <>
                                {!isChecked && (
                                    <Button
                                        type="button"
                                        size="small"
                                        variant="primary-ghost"
                                        disabled={false}
                                        onClick={onCancel}
                                    >
                                        <span>ODUSTANI</span>
                                    </Button>
                                )}
                                <Button
                                    type="button"
                                    size="small"
                                    variant="primary"
                                    disabled={isButtonDisabled}
                                    onClick={onSave}
                                >
                                    <span>SPREMI</span>
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    type="button"
                                    size="small"
                                    variant="warning-ghost"
                                    disabled={profile?.roleType?.value !== RoleType.SuperAdmin}
                                    onClick={() => setShowDelete(true)}
                                >
                                    <span>OBRIŠI</span>
                                </Button>
                                <Button
                                    type="button"
                                    size="small"
                                    variant="primary-ghost"
                                    disabled={profile?.roleType?.value !== RoleType.SuperAdmin}
                                    onClick={() => setIsEdit(true)}
                                >
                                    <span>UREDI</span>
                                </Button>
                            </>
                        )}
                    </ModalFooter>
                ))
            }
        >
            <div className="education-document-details">
                {!isBulkEdit && (
                    <div className="modal-section">
                        <Field
                            name={
                                isAdd
                                    ? "DODAJ DATOTEKU"
                                    : isEdit || isChecked
                                    ? "ZAMIJENI DATOTEKU"
                                    : "DATOTEKA"
                            }
                            errorMessage={getErrorMsg("fileUrl")}
                        >
                            {isAdd || isEdit || isChecked ? (
                                <Dropzone
                                    handleDrop={(files: File[]) => {
                                        setEducationDocument(prevState => ({
                                            ...(prevState ?? emptyEducationDocument),
                                            uploadedFile: files[0],
                                            name: files[0]?.name || prevState.name,
                                        }));
                                        setErrors(prevState => ({
                                            ...prevState,
                                            fileUrl: undefined,
                                        }));
                                    }}
                                    handleDelete={_ => {
                                        setEducationDocument(prevState => ({
                                            ...(prevState ?? emptyEducationDocument),
                                            uploadedFile: null,
                                        }));
                                    }}
                                    files={educationDocument.uploadedFile ? [educationDocument.uploadedFile] : []}
                                />
                            ) : (
                                <span
                                    className={
                                        lowVision ? "low-vision-dropzone" : "dropzone-file-name"
                                    }
                                >
                                    {educationDocument?.name}
                                </span>
                            )}
                        </Field>
                    </div>
                )}
                <div className="modal-section">
                    <Field name="KATEGORIJA" errorMessage={getErrorMsg("categoryGuid")}>
                        <DisplayOrInput isEdit={isAdd || isEdit || isChecked}>
                            {educationDocumentCategory?.label}
                            <SearchableDropdown
                                items={[
                                    { label: "Nova kategorija", id: "new" },
                                    ...(educationDocumentCategoriesAsItems ?? []),
                                ]}
                                value={educationDocumentCategory}
                                noOptionsMessage={() => "Nema pronađenih kategorija"}
                                labelKey="label"
                                onSelect={item =>
                                    setEducationDocument(prevState => ({
                                        ...prevState,
                                        categoryGuid: item?.id ?? "",
                                    }))
                                }
                                placeholder="Pretraživanje"
                                customIcons={{
                                    searchIcon: <SearchIcon width={14} height={14} />,
                                }}
                                onChange={(value: { value: string; label: string }) => {
                                    setIsNewCategory(value.value === "new");
                                    changeEducationDocumentDropdownProperty("categoryGuid", value);
                                }}
                            />
                        </DisplayOrInput>
                    </Field>

                    {isNewCategory && (
                        <Field name="NAZIV KATEGORIJE" errorMessage={getErrorMsg(catName)}>
                            <DisplayOrInput isEdit={isAdd || isEdit || isChecked}>
                                {educationDocument?.categoryName}
                                <Input
                                    id={catName}
                                    type="text"
                                    size="small"
                                    name={catName}
                                    value={educationDocument?.categoryName}
                                    onChange={changeEducationDocumentProperty.bind(null, catName)}
                                />
                            </DisplayOrInput>
                        </Field>
                    )}
                </div>
                <div className="modal-section">
                    <Field
                        name="STRUČNO PODRUČJE"
                        errorMessage={getErrorMsg("professionalAreaGuid")}
                    >
                        <DisplayOrInput isEdit={isAdd || isEdit || isChecked}>
                            {professionalArea?.label}
                            <SearchableDropdown
                                items={professionalAreasAsItems ?? []}
                                value={professionalArea}
                                noOptionsMessage={() => "Nema pronađenih stručnih područja"}
                                labelKey="label"
                                onSelect={item =>
                                    setEducationDocument(prevState => ({
                                        ...prevState,
                                        professionalAreaGuid: item?.guid ?? "",
                                    }))
                                }
                                placeholder="Pretraživanje"
                                customIcons={{
                                    searchIcon: <SearchIcon width={14} height={14} />,
                                }}
                            />
                        </DisplayOrInput>
                    </Field>
                </div>
                {!isBulkEdit && (
                    <div className="modal-section">
                        <Field name="NAZIV DOKUMENTA" errorMessage={getErrorMsg("name")}>
                            <DisplayOrInput isEdit={isAdd || isEdit || isChecked}>
                                {educationDocument?.name}
                                <Input
                                    id="name"
                                    type="text"
                                    size="small"
                                    name="name"
                                    value={educationDocument?.name}
                                    onChange={changeEducationDocumentProperty.bind(null, "name")}
                                />
                            </DisplayOrInput>
                        </Field>
                    </div>
                )}
                {(educationDocument.created !== "" || educationDocument.lastUpdated !== "") &&
                    profile?.roleType?.value === RoleType.SuperAdmin && (
                        <>
                            <div className="modal-section narrow">
                                <FingerprintDisplay
                                    item={educationDocument}
                                    value="created"
                                    containerClassName="modal-section no-border"
                                    labelClassName="modal-details-label"
                                    textClassName="modal-details-text"
                                />
                            </div>
                            <div className="modal-section narrow">
                                <FingerprintDisplay
                                    item={educationDocument}
                                    value="lastUpdated"
                                    containerClassName="modal-section no-border"
                                    labelClassName="modal-details-label"
                                    textClassName="modal-details-text"
                                />
                            </div>
                        </>
                    )}
            </div>
            <ConfirmationModal
                isActive={showDelete}
                onConfirm={onDelete}
                onCancel={() => setShowDelete(false)}
                content={`Želite li izbrisati dokument ${educationDocument?.name}?`}
                confirmText="OBRIŠI"
                cancelText="ODUSTANI"
            />
        </Modal>
    );
};
